import { ConfirmDialog } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import type { TypedClient } from '@platform/openapi';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { useRef } from 'react';
import { ProductEditorPage } from '../features/catalog/ProductEditorPage.tsx';
import { PRODUCTS_DEFAULTS } from './products-search.ts';

export const ProductEditorRoute = ({ api, productId }: { api: TypedClient; productId: string | null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dirty = useRef(false);

  const blocker = useBlocker({
    shouldBlockFn: () => dirty.current,
    enableBeforeUnload: () => dirty.current,
    withResolver: true,
  });

  const leave = () => {
    dirty.current = false;
    void navigate({ to: '/products', search: PRODUCTS_DEFAULTS });
  };

  return (
    <>
      {blocker.status === 'blocked' ? (
        <ConfirmDialog
          title={t('productEditor.leaveTitle')}
          message={t('productEditor.unsavedChanges')}
          confirmLabel={t('productEditor.leave')}
          cancelLabel={t('productEditor.stay')}
          closeLabel={t('common.close')}
          onConfirm={blocker.proceed}
          onCancel={blocker.reset}
        />
      ) : null}
      <ProductEditorPage
        api={api}
        productId={productId}
        onBack={() => {
          void navigate({ to: '/products', search: PRODUCTS_DEFAULTS });
        }}
        onArchived={leave}
        onDirtyChange={(isDirty) => {
          dirty.current = isDirty;
        }}
        onSaved={(saved, outcome) => {
          dirty.current = false;
          if (outcome === 'created')
            void navigate({
              to: '/products',
              search: { ...PRODUCTS_DEFAULTS, created: saved.id },
              replace: true,
            });
        }}
      />
    </>
  );
};
